import { CommonModule, NgOptimizedImage } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { EBookService, ArticleService, Article, ArticleType } from '../../admin/shared/public-api';
import { TemplateDirective } from '../../admin/templates/template.directive';
import { ArticleSection } from '../../articles/article/models/article-section';
import { NavService } from '../../nav/service/nav.service';
import { ArticleCardComponent } from '../../shared/components/article-card/article-card.component';
import { ArticleDirective } from '../../shared/directives/article.directive';
import { DeviceService } from '../../shared/services/device.service';
import { MetaService } from '../../shared/services/meta.service';
import { JSONLDService } from '../../shared/services/jsonld.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-ebook',
  imports: [
    CommonModule,
    FormsModule,
    NgOptimizedImage,
    ArticleDirective,
    TemplateDirective,
    ArticleCardComponent,
  ],
  templateUrl: './ebook.component.html',
  styleUrl: './ebook.component.scss',
})
export class EbookComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild(ArticleDirective) articleDirective: ArticleDirective;

  navService = inject(NavService);
  ebookService = inject(EBookService);
  articleService = inject(ArticleService);
  jsonLdService = inject(JSONLDService);
  titleService = inject(Title);
  deviceService = inject(DeviceService);
  private readonly metaTagService = inject(MetaService);

  name: string;
  email: string;
  submitted: boolean;
  discoverArticles: Article[] = [];

  ebookSections: ArticleSection<any>[] = [
    {
      componentName: 'main-title',
      id: '1',
      order: 0,
      sectionData: {
        icon: 'hike.svg',
        title: `<p>Où se balader en Belgique ?</p>`,
        id: 't-1',
      },
      articleId: '',
      customClass: '',
    },
    {
      componentName: 'paragraph',
      id: '2',
      order: 1,
      sectionData: {
        text: `
        Voilà maintenant plus de 3 ans que nous découvrons la Belgique à travers ses sentiers de randonnée. 
        Une activité enrichissante et gratuite qui nous a fait découvrir notre pays autrement et qui nous a révélé ses plus beaux paysages. 
        Dans ce guide gratuit (disponible sur mobile et desktop), nous te partageons nos 5 plus belles balades belges. 
        Il t’invitera à sortir des sentiers battus et découvrir la Belgique comme tu ne l’as jamais vue.        
        `,
      },
      articleId: '',
      customClass: '',
    },
    {
      componentName: 'info',
      id: '3',
      order: 2,
      sectionData: {
        title: `Ce que comprend ce guide de randonnées en Belgique`,
        text: `
        📕 5 randonnées en Belgique <br>
        🗺️ Une carte avec les points d’intérêts <br>
        📈 Les tracés GPX et/ou balisages <br>
        🅿️ Les parkings à proximités <br>  
        `,
      },
      articleId: '',
      customClass: '',
    },
    {
      componentName: 'paragraph',
      id: '4',
      order: 3,
      sectionData: {
        text: `
        En bref, nous avons regroupé toutes les infos pratiques pour chaque randonnée. 
        Tu n’as plus qu’à enfiler tes chaussures de randonnée et partir à la découverte de la Belgique.     
        `,
      },
      articleId: '',
      customClass: '',
    },
  ];

  ngOnInit(): void {
    this.titleService.setTitle(`Les Explorateurs du Monde - Ebook: 5 randonnées en Belgique`);

    const baseUrl = environment.baseUrl;

    this.metaTagService.updateMetaTags(
      `Un ebook gratuit regroupant 5 belles randonnées à faire en Belgique. Inspiration de randonnées.`,
      `Les Explorateurs du Monde - Ebook: 5 randonnées en Belgique`,
      `${baseUrl}ebooks/5-randonnees-belgique`,
      'article',
      `${baseUrl}/ebook/ebook-desktop.webp`,
      411,
      584,
      'image/jpeg',
      new Date(2024, 4, 4, 0, 0, 0, 0).toString(),
      new Date(2024, 4, 4, 0, 0, 0, 0).toString()
    );

    const article = new Article();
    article.name = `Les Explorateurs du Monde - Ebook: 5 randonnées en Belgique`;
    article.description = `Un ebook gratuit regroupant 5 belles randonnées à faire en Belgique. Inspiration de randonnées.`;
    article.coverPicture = {
      src: `${baseUrl}/ebook/ebook-desktop.webp`,
      width: 411,
      height: 584,
      description: `Un ebook gratuit regroupant 5 belles randonnées à faire en Belgique. Inspiration de randonnées.`,
      legend: '',
      name: 'Les Explorateurs du Monde - Ebook: 5 randonnées en Belgique',
      order: 0,
    };
    article.id = '5-randonnees-belgique';
    article.slug = '5-randonnees-belgique';
    this.jsonLdService.insertSchema(article);

    this.navService.setGreenBackground();
    this.articleService
      .getRandomArticles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((a) => (this.discoverArticles = a));
  }

  ngAfterViewInit(): void {
    this.articleDirective.initDirective();
  }

  downloadEbook(): void {
    this.submitted = true;
    this.ebookService.downloadEbook({ name: this.name, email: this.email }).subscribe({
      next: () => {
        console.log('downloaded');
      },
    });
  }
}
